import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, DollarSign, CheckCircle, Clock, AlertCircle, Search, Phone, Mail, MessageSquare, FileText } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import Icon from '../../components/AppIcon';


export default function BarrelCustomerOrdersManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newOrder, setNewOrder] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    customerCity: '',
    postalCode: '',
    deliveryTimeSlot: '',
    barrelType: 'noir_270l',
    quantity: 1,
    deliveryDate: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordersData, statsData] = await Promise.all([
        barrelService?.getBarrelOrders(),
        barrelService?.getBarrelStatistics()
      ]);
      setOrders(ordersData);
      setStatistics(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    try {
      const barrelPrices = {
        noir_270l: 35,
        bleu_220l: 40
      };
      const unitPrice = barrelPrices?.[newOrder?.barrelType];
      const totalPrice = unitPrice * parseInt(newOrder?.quantity);

      const orderData = {
        customer_name: newOrder?.customerName,
        customer_phone: newOrder?.customerPhone,
        customer_email: newOrder?.customerEmail,
        customer_address: newOrder?.customerAddress,
        customer_city: newOrder?.customerCity,
        postal_code: newOrder?.postalCode,
        delivery_time_slot: newOrder?.deliveryTimeSlot,
        barrel_type: newOrder?.barrelType,
        quantity: parseInt(newOrder?.quantity),
        unit_price: unitPrice,
        total_price: totalPrice,
        status: 'pending',
        payment_status: 'pending',
        delivery_date: newOrder?.deliveryDate || null,
        notes: newOrder?.notes
      };

      await barrelService?.createBarrelOrder(orderData);
      await loadData();
      setShowNewOrderModal(false);
      setNewOrder({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        customerAddress: '',
        customerCity: '',
        postalCode: '',
        deliveryTimeSlot: '',
        barrelType: 'noir_270l',
        quantity: 1,
        deliveryDate: '',
        notes: ''
      });
      
      // Show success message indicating automatic sync
      alert('Commande créée avec succès! Elle est maintenant disponible dans le système de planification.');
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Erreur lors de la création de la commande');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      const updates = { status };
      if (status === 'delivered') {
        updates.delivery_date = new Date()?.toISOString()?.split('T')?.[0];
      }
      await barrelService?.updateBarrelOrder(orderId, updates);
      await loadData();
      
      // Show success message for confirmed orders
      if (status === 'confirmed') {
        alert('Commande confirmée! Elle apparaîtra automatiquement dans le système de planification.');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleUpdatePaymentStatus = async (orderId, paymentStatus) => {
    try {
      const updates = { payment_status: paymentStatus };
      if (paymentStatus === 'paid') {
        updates.payment_date = new Date()?.toISOString()?.split('T')?.[0];
      }
      await barrelService?.updateBarrelOrder(orderId, updates);
      await loadData();
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert('Erreur lors de la mise à jour du paiement');
    }
  };

  const handleSendWhatsAppMessage = (order) => {
    const message = `Bonjour ${order?.customer_name}, votre commande de ${order?.quantity} ${getBarrelTypeLabel(order?.barrel_type)} est confirmée. Total: ${order?.total_price}€. Merci!`;
    const whatsappUrl = `https://wa.me/${order?.customer_phone?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window?.open(whatsappUrl, '_blank');
  };

  const handleViewOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowOrderDetailModal(true);
  };

  const getBarrelTypeLabel = (type) => {
    return type === 'noir_270l' ? 'Barrique Noire 270L (35€)' : 'Barrique Bleue 220L (40€)';
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      prepared: { label: 'Préparée', color: 'bg-purple-100 text-purple-800', icon: Package },
      delivered: { label: 'Livrée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    const badge = badges?.[status] || badges?.pending;
    const Icon = badge?.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${badge?.color}`}>
        <Icon className="w-3 h-3" />
        {badge?.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      partial: { label: 'Partiel', color: 'bg-orange-100 text-orange-800' },
      paid: { label: 'Payé', color: 'bg-green-100 text-green-800' },
      refunded: { label: 'Remboursé', color: 'bg-gray-100 text-gray-800' }
    };
    const badge = badges?.[status] || badges?.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge?.color}`}>
        {badge?.label}
      </span>
    );
  };

  const filteredOrders = orders?.filter(order => {
    if (filterStatus !== 'all' && order?.status !== filterStatus) return false;
    if (filterPaymentStatus !== 'all' && order?.payment_status !== filterPaymentStatus) return false;
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      return (
        order?.customer_name?.toLowerCase()?.includes(search) ||
        order?.customer_phone?.toLowerCase()?.includes(search) ||
        order?.customer_email?.toLowerCase()?.includes(search)
      );
    }
    return true;
  });

  const pendingCount = orders?.filter(o => o?.status === 'pending')?.length || 0;
  const confirmedCount = orders?.filter(o => o?.status === 'confirmed')?.length || 0;
  const deliveredCount = orders?.filter(o => o?.status === 'delivered')?.length || 0;
  const totalRevenue = orders?.filter(o => o?.payment_status === 'paid')?.reduce((sum, o) => sum + parseFloat(o?.total_price || 0), 0) || 0;
  const pendingPayments = orders?.filter(o => o?.payment_status === 'pending')?.reduce((sum, o) => sum + parseFloat(o?.total_price || 0), 0) || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <ShoppingCart className="w-8 h-8 text-green-600" />
            Gestion Commandes Barriques & Cartons Clients
          </h1>
          <p className="mt-2 text-gray-600">
            Traitement complet des commandes de barriques et de cartons vides - toutes les commandes de barriques et de cartons peuvent être gérées ici
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En attente</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Confirmées</p>
                <p className="text-2xl font-bold text-blue-600">{confirmedCount}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Livrées</p>
                <p className="text-2xl font-bold text-green-600">{deliveredCount}</p>
              </div>
              <Package className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Revenu Total</p>
                <p className="text-2xl font-bold text-green-600">{totalRevenue?.toFixed(2)}€</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paiements en attente</p>
                <p className="text-2xl font-bold text-orange-600">{pendingPayments?.toFixed(2)}€</p>
              </div>
              <AlertCircle className="w-10 h-10 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col md:flex-row gap-4 flex-1 w-full">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, téléphone, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="confirmed">Confirmée</option>
                <option value="prepared">Préparée</option>
                <option value="delivered">Livrée</option>
                <option value="cancelled">Annulée</option>
              </select>

              <select
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="all">Tous paiements</option>
                <option value="pending">En attente</option>
                <option value="partial">Partiel</option>
                <option value="paid">Payé</option>
              </select>
            </div>

            <button
              onClick={() => setShowNewOrderModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Package className="w-5 h-5" />
              Nouvelle Commande
            </button>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commande
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut Commande
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paiement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p>Aucune commande trouvée</p>
                    </td>
                  </tr>
                ) : (
                  filteredOrders?.map((order) => (
                    <tr key={order?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">#{order?.id?.slice(0, 8)}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(order?.created_at)?.toLocaleDateString('fr-FR')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{order?.customer_name}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                          <Phone className="w-3 h-3" />
                          {order?.customer_phone}
                        </div>
                        {order?.customer_email && (
                          <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                            <Mail className="w-3 h-3" />
                            {order?.customer_email}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {getBarrelTypeLabel(order?.barrel_type)}
                        </div>
                        <div className="text-xs text-gray-500">Quantité: {order?.quantity}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{order?.total_price}€</div>
                        <div className="text-xs text-gray-500">{order?.unit_price}€ / unité</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(order?.status)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={order?.payment_status}
                          onChange={(e) => handleUpdatePaymentStatus(order?.id, e?.target?.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="pending">En attente</option>
                          <option value="partial">Partiel</option>
                          <option value="paid">Payé</option>
                          <option value="refunded">Remboursé</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order?.delivery_date ? new Date(order?.delivery_date)?.toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          {order?.status === 'pending' && (
                            <button
                              onClick={() => handleUpdateOrderStatus(order?.id, 'confirmed')}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 font-medium"
                              title="Confirmer et synchroniser vers le planning"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Confirmer
                            </button>
                          )}
                          {order?.status === 'confirmed' && (
                            <button
                              onClick={() => handleUpdateOrderStatus(order?.id, 'prepared')}
                              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1.5"
                            >
                              <Package className="w-4 h-4" />
                              Préparer
                            </button>
                          )}
                          {order?.status === 'prepared' && (
                            <button
                              onClick={() => handleUpdateOrderStatus(order?.id, 'delivered')}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1.5"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Livrer
                            </button>
                          )}
                          <button
                            onClick={() => handleViewOrderDetail(order)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Voir détails"
                          >
                            <FileText className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleSendWhatsAppMessage(order)}
                            className="text-green-600 hover:text-green-800"
                            title="Envoyer WhatsApp"
                          >
                            <MessageSquare className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* New Order Modal */}
      {showNewOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Nouvelle Commande de Barriques</h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du client *
                    </label>
                    <input
                      type="text"
                      value={newOrder?.customerName}
                      onChange={(e) => setNewOrder({...newOrder, customerName: e?.target?.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      value={newOrder?.customerPhone}
                      onChange={(e) => setNewOrder({...newOrder, customerPhone: e?.target?.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newOrder?.customerEmail}
                    onChange={(e) => setNewOrder({...newOrder, customerEmail: e?.target?.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse de livraison *
                  </label>
                  <input
                    type="text"
                    value={newOrder?.customerAddress}
                    onChange={(e) => setNewOrder({...newOrder, customerAddress: e?.target?.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville *
                  </label>
                  <input
                    type="text"
                    value={newOrder?.customerCity}
                    onChange={(e) => setNewOrder({...newOrder, customerCity: e?.target?.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code postal
                  </label>
                  <input
                    type="text"
                    value={newOrder?.postalCode}
                    onChange={(e) => setNewOrder({...newOrder, postalCode: e?.target?.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: 75001"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de barrique *
                    </label>
                    <select
                      value={newOrder?.barrelType}
                      onChange={(e) => setNewOrder({...newOrder, barrelType: e?.target?.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="noir_270l">Barrique Noire 270L - 35€</option>
                      <option value="bleu_220l">Barrique Bleue 220L - 40€</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantité *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newOrder?.quantity}
                      onChange={(e) => setNewOrder({...newOrder, quantity: e?.target?.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de livraison souhaitée
                  </label>
                  <input
                    type="date"
                    value={newOrder?.deliveryDate}
                    onChange={(e) => setNewOrder({...newOrder, deliveryDate: e?.target?.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tranche horaire de livraison souhaitée
                  </label>
                  <select
                    value={newOrder?.deliveryTimeSlot}
                    onChange={(e) => setNewOrder({...newOrder, deliveryTimeSlot: e?.target?.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Sélectionner une tranche horaire</option>
                    <option value="08h-12h">08h - 12h (Matin)</option>
                    <option value="12h-14h">12h - 14h (Midi)</option>
                    <option value="14h-18h">14h - 18h (Après-midi)</option>
                    <option value="18h-20h">18h - 20h (Soir)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={newOrder?.notes}
                    onChange={(e) => setNewOrder({...newOrder, notes: e?.target?.value})}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Prix unitaire:</span>
                    <span className="text-lg font-bold text-gray-900">
                      {newOrder?.barrelType === 'noir_270l' ? '35' : '40'}€
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-medium text-gray-700">Total:</span>
                    <span className="text-xl font-bold text-green-600">
                      {(newOrder?.barrelType === 'noir_270l' ? 35 : 40) * parseInt(newOrder?.quantity || 1)}€
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowNewOrderModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateOrder}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Créer la Commande
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {showOrderDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Détails Commande</h2>
                  <p className="text-sm text-gray-500">#{selectedOrder?.id}</p>
                </div>
                <button
                  onClick={() => setShowOrderDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Informations Client</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-gray-600">Nom</p>
                      <p className="font-medium text-gray-900">{selectedOrder?.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Téléphone</p>
                      <p className="font-medium text-gray-900">{selectedOrder?.customer_phone}</p>
                    </div>
                    {selectedOrder?.customer_email && (
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium text-gray-900">{selectedOrder?.customer_email}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600">Adresse</p>
                      <p className="font-medium text-gray-900">
                        {selectedOrder?.customer_address}, {selectedOrder?.customer_city}
                        {selectedOrder?.postal_code && ` ${selectedOrder?.postal_code}`}
                      </p>
                    </div>
                    {selectedOrder?.delivery_time_slot && (
                      <div>
                        <p className="text-sm text-gray-600">Tranche horaire souhaitée</p>
                        <p className="font-medium text-gray-900">{selectedOrder?.delivery_time_slot}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Détails Commande</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Produit:</span>
                      <span className="font-medium text-gray-900">{getBarrelTypeLabel(selectedOrder?.barrel_type)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Quantité:</span>
                      <span className="font-medium text-gray-900">{selectedOrder?.quantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Prix unitaire:</span>
                      <span className="font-medium text-gray-900">{selectedOrder?.unit_price}€</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-semibold text-gray-900">Total:</span>
                      <span className="font-bold text-green-600 text-lg">{selectedOrder?.total_price}€</span>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">Statut Commande</p>
                    {getStatusBadge(selectedOrder?.status)}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">Statut Paiement</p>
                    {getPaymentStatusBadge(selectedOrder?.payment_status)}
                  </div>
                </div>

                {/* Dates */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Dates</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Commande créée:</span>
                      <span className="font-medium text-gray-900">
                        {new Date(selectedOrder?.created_at)?.toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    {selectedOrder?.delivery_date && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date de livraison:</span>
                        <span className="font-medium text-gray-900">
                          {new Date(selectedOrder?.delivery_date)?.toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    )}
                    {selectedOrder?.payment_date && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date de paiement:</span>
                        <span className="font-medium text-gray-900">
                          {new Date(selectedOrder?.payment_date)?.toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                {selectedOrder?.notes && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
                    <p className="text-gray-700">{selectedOrder?.notes}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => handleSendWhatsAppMessage(selectedOrder)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <MessageSquare className="w-5 h-5" />
                  Envoyer WhatsApp
                </button>
                <button
                  onClick={() => setShowOrderDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}